import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { CloseOutlined } from '@ant-design/icons';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { SITE_SURVEY_ANSWER_TYPE } from '../../utils/SiteSurveyUtils';
import WalkthroughTaskListAnswerDisplay from '../walkthroughs_detail_details_page/WalkthroughTaskListAnswerDisplay';
import OWInsightPopover from '../ow_insight_popover/OWInsightPopover';
import { UNFINISHED_WORK_ORDER_DISPLAY_STATUS_DISPLAY_NAMES } from '../../constants/WorkOrderDisplayStatuses';

// Single source of truth for how a walkthrough task answer's content is rendered. The Overview
// table (WalkthroughsDetailDetailsPage) and the note embed (walkthrough Notes tab) both consume
// these so the task always looks the same wherever it is shown.

// Inline answer shown directly under the title — used for free-text / non multiple-choice,
// non task-list, non checklist questions. Returns null for the types that render their answer
// in their own block (see renderTaskAnswer).
export const renderTaskInlineAnswer = (record) => {
	switch (record.questionType) {
		case SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE:
		case SITE_SURVEY_ANSWER_TYPE.TASK_LIST:
		case SITE_SURVEY_ANSWER_TYPE.CHECKLIST:
			return null;
		default:
			return (
				<div className="walkthroughRowDisplay__rowTitle mt-2 text-gray-600">
					<div className="walkthroughRowDisplay__rowInlineGroup">
						{nullSafeGetOrElse('answer', record, '--')}
					</div>
				</div>
			);
	}
};

// The "answer" block — the multiple-choice answer, or the task-list / checklist item display.
export const renderTaskAnswer = (record, walkthrough, checkable) => {
	switch (record.questionType) {
		case SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE:
			return (
				<div className="walkthroughRowDisplay__rowSubtitle ml-2 text-gray-600">
					<div className="walkthroughRowDisplay__rowInlineGroup">
						{nullSafeGetOrElse('answer', record, '--')}
					</div>
				</div>
			);
		case SITE_SURVEY_ANSWER_TYPE.TASK_LIST:
		case SITE_SURVEY_ANSWER_TYPE.CHECKLIST:
			return (
				<WalkthroughTaskListAnswerDisplay
					taskAnswer={record}
					timeZoneId={nullSafeGet('location.timeZoneId', walkthrough)}
					checkable={checkable}
				/>
			);
		default:
			return null;
	}
};

// Comment count + attachment count + work-order popover, mirroring the Overview row's icon group.
export const TaskCountsDisplay: FC<any> = ({ record }) => {
	const numComments = record.comments ? 1 : 0;
	const attachments = nullSafeGetOrElse('attachments', record, []);
	const numAttachments = attachments.length;
	return (
		<div className="flex flex-row items-center" style={{ gap: 16 }}>
			{numComments > 0 ? (
				<div className="flex flex-row items-center">
					<div style={{ minWidth: 10, margin: '0px' }}>{numComments}</div>
					<i
						style={{
							fontSize: 20,
							verticalAlign: 'middle',
							color: '#999',
							margin: '0px 2px',
							paddingLeft: '4px',
							paddingRight: '4px',
						}}
						className="icons8-font icons8-speech-bubble"
					/>
				</div>
			) : null}
			{numAttachments > 0 ? (
				<div className="flex flex-row items-center">
					<div style={{ minWidth: 10, margin: '0px' }}>{numAttachments}</div>
					<i
						style={{
							fontSize: 20,
							verticalAlign: 'middle',
							color: '#999',
							margin: '0px 2px',
							paddingLeft: '4px',
							paddingRight: '4px',
						}}
						className="icons8-font icons8-compact-camera"
					/>
				</div>
			) : null}
			<OWInsightPopover
				title={'Work Orders'}
				breakdown={'displayStatus'}
				labels={UNFINISHED_WORK_ORDER_DISPLAY_STATUS_DISPLAY_NAMES}
				icon={'icons8-wrench'}
				defaultData={record.workOrders || []}
			/>
		</div>
	);
};

interface WalkthroughTaskSummaryDisplayProps {
	/** The task answer record (from walkthrough.taskAnswers). */
	record: any;
	/** Parent walkthrough — needed for the task-list/checklist answer display and the deep link. */
	walkthrough: any;
	userType: string;
	/** Prefix the title with the task's group, e.g. "GARAGE: ...". */
	showGroupPrefix?: boolean;
	/** Render the answer block under the title. */
	showAnswer?: boolean;
	/** Render the comment/attachment/work-order counts. */
	showCounts?: boolean;
	/** Render a "Go to task" deep link to the matched task on the Overview tab. */
	showGoToTask?: boolean;
	/** When provided, an X is rendered to dismiss the summary (used by the compose chip). */
	onDismiss?: () => void;
	/** Override the container styling. Defaults to the bordered card used by the sent-note embed; the
	 *  compose chip passes a borderless grey banner. */
	wrapperClassName?: string;
	/** Constrain the title to a single line with an ellipsis (used by the compose chip banner). */
	truncateTitle?: boolean;
}

const DEFAULT_WRAPPER_CLASS_NAME =
	'rounded-lg border border-solid border-grey-5 bg-grey-1 px-3 py-2';

// Compact card used to surface a referenced task — in the Notes compose area (dismissible) and
// inside a persisted note (with a "Go to task" deep link back to the Overview).
const WalkthroughTaskSummaryDisplay: FC<WalkthroughTaskSummaryDisplayProps> = ({
	record,
	walkthrough,
	userType,
	showGroupPrefix = false,
	showAnswer = false,
	showCounts = false,
	showGoToTask = false,
	onDismiss,
	wrapperClassName = DEFAULT_WRAPPER_CLASS_NAME,
	truncateTitle = false,
}) => {
	if (!record) return null;

	const groupTitle = nullSafeGet('groupTitle', record);
	const hasGroup = showGroupPrefix && groupTitle && groupTitle !== 'undefined';
	const goToTaskLink = `/${userType}/siteSurveys/walkthrough/detail/${nullSafeGet(
		'id',
		walkthrough
	)}/details?taskTitleId=${encodeURIComponent(nullSafeGetOrElse('titleId', record, ''))}`;

	return (
		<div className={wrapperClassName}>
			<div
				className={`flex flex-row justify-between gap-2 ${
					truncateTitle ? 'items-center' : 'items-start'
				}`}
			>
				<div className="min-w-0 flex-1">
					{/* Compose chip (truncateTitle): the group is the bold prefix and the title is regular
					    weight, e.g. "GARAGE: Door check", on a single truncated line. The sent-note card keeps
					    its medium title with a secondary-coloured group. */}
					<span
						className={`text-character-primary ${
							truncateTitle ? 'bodyregular block truncate' : 'bodymedium'
						}`}
					>
						{hasGroup ? (
							<span className={truncateTitle ? 'bodybold' : 'text-character-secondary'}>
								{groupTitle}:{' '}
							</span>
						) : null}
						{nullSafeGet('title', record)}
					</span>
					{showGoToTask ? (
						<Link to={goToTaskLink} className="bodymedium ml-2 whitespace-nowrap">
							Go to task
						</Link>
					) : null}
				</div>
				{onDismiss ? (
					<CloseOutlined
						className="shrink-0 cursor-pointer text-character-secondary"
						onClick={onDismiss}
					/>
				) : null}
			</div>
			{showAnswer ? (
				<div className="mt-1">
					{renderTaskInlineAnswer(record) || renderTaskAnswer(record, walkthrough, false)}
				</div>
			) : null}
			{showCounts ? (
				<div className="mt-2">
					<TaskCountsDisplay record={record} />
				</div>
			) : null}
		</div>
	);
};

export default WalkthroughTaskSummaryDisplay;
